import React from 'react';
import {
  Box,
  Typography,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { colors } from '../../constants/globalcss';
import { sizes } from '../../styles/commonStyles';

interface NdcGroupedDocketsInfoProps {
  docketInfo: string;
}

function parseDocketInfo(str: string) {
  const sets = str
    .replace(/<br\s*\/?>/gi, '\n')
    .split(/\n\s*\n/)
    .map((s) => s.trim())
    .filter(Boolean);

  return sets.map((set) => {
    const lines = set
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    const result: {
      description?: string;
      docketNo?: string;
      documentSequenceNo?: string;
      enteredDate?: string;
      link?: string;
    } = {};

    lines.forEach((line) => {
      if (line.startsWith('Description:')) {
        result.description = line.replace('Description:', '').trim();
      } else if (line.startsWith('Docket No:')) {
        const docketMatch = line.match(/Docket No:\s*(\d+)/);
        const docSeqMatch = line.match(/Document Sequence No:\s*(\d+)/);
        const dateMatch = line.match(/Entered Date:\s*([\d-]+)/);
        if (docketMatch) result.docketNo = docketMatch[1];
        if (docSeqMatch) result.documentSequenceNo = docSeqMatch[1];
        if (dateMatch) result.enteredDate = dateMatch[1];
      } else if (line.startsWith('Link:')) {
        result.link = line.replace('Link:', '').trim();
      }
    });

    return result;
  });
}

function getShortUrl(url?: string) {
  if (!url) return '';
  try {
    const u = new URL(url);
    const segments = u.pathname.split('/').filter(Boolean);
    const lastSegment = segments.length ? segments[segments.length - 1] : '';
    return `${u.hostname}/${lastSegment}`;
  } catch {
    return url;
  }
}

const columns = [
  { key: 'description', label: 'Description' },
  { key: 'docketNo', label: 'Docket No' },
  { key: 'documentSequenceNo', label: 'Document Sequence No' },
  { key: 'enteredDate', label: 'Entered Date' },
  { key: 'link', label: 'Link' },
];

const NdcGroupedDocketsInfo: React.FC<NdcGroupedDocketsInfoProps> = ({
  docketInfo,
}) => {
  const parsedInfos = parseDocketInfo(docketInfo);

  return (
    <Box
      sx={{
        mb: sizes.spacing.md,
        mt: sizes.spacing.md,
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{
          color: colors.primary,
          fontSize: sizes.fontSize.body,
          fontWeight: 600,
          letterSpacing: sizes.letterSpacing.wide,
          mb: sizes.spacing.md,
        }}
      >
        NDC Dockets Info
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          background: colors.background.paper,
          borderRadius: sizes.borderRadius.medium,
          boxShadow: `0 2px 8px rgba(25, 118, 210, 0.07)`,
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: colors.primary }}>
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  sx={{
                    color: colors.background.paper,
                    fontWeight: 700,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedInfos.map((info, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{
                      color:
                        col.key === 'link'
                          ? colors.primary
                          : colors.text.primary,
                      fontWeight: col.key === 'link' ? 500 : undefined,
                      wordBreak: col.key === 'link' ? 'break-all' : undefined,
                    }}
                  >
                    {col.key === 'link' && info.link ? (
                      <Link
                        href={info.link}
                        target="_blank"
                        rel="noopener"
                        underline="hover"
                        sx={{
                          color: colors.primary,
                          fontWeight: 600,
                          wordBreak: 'break-all',
                        }}
                      >
                        {getShortUrl(info.link)}
                      </Link>
                    ) : (
                      info[col.key as keyof typeof info]
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default NdcGroupedDocketsInfo;
